import { useEffect } from 'react';
import { create } from 'zustand';

interface IStore {
  isPaused: boolean;
  isFinished: boolean;
  remainingTime?: number;
  countdown?: number;
  spentTime: number;
  setSpentTime: (spentTime: number) => void;
  setRemainingTime: (remainingTime?: number) => void;
  setCountdown: (countdown: number | undefined) => void;
  setIsPaused: (isPaused: boolean) => void;
  setIsFinished: (isFinished: boolean) => void;
  reset: () => void;
}

const store = create<IStore>(set => ({
  isPaused: true,
  isFinished: false,
  spentTime: 0,
  setRemainingTime: remainingTime => set({ remainingTime }),
  setCountdown: countdown => set({ countdown, isPaused: false }),
  setIsPaused: isPaused => set({ isPaused }),
  setIsFinished: isFinished => set({ isFinished }),
  setSpentTime: spentTime => set({ spentTime }),
  reset: () =>
    set({
      countdown: undefined,
      isPaused: true,
      remainingTime: undefined,
      isFinished: false,
      spentTime: 0
    })
}));

export const useCountdown = () => {
  const {
    isPaused,
    remainingTime,
    setRemainingTime,
    countdown,
    setCountdown,
    setIsPaused,
    reset,
    isFinished,
    setIsFinished,
    spentTime,
    setSpentTime
  } = store();

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (!isPaused) {
      interval = setInterval(() => {
        setSpentTime(spentTime + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPaused, spentTime]);

  const setTimer = (newTimeInMinutes?: number | null) => {
    if (typeof newTimeInMinutes === 'number' && !isNaN(newTimeInMinutes)) {
      setCountdown(Date.now() + newTimeInMinutes * 60 * 1000);
    } else {
      setCountdown(undefined);
      setIsPaused(false);
    }
  };

  const toggle = () => {
    if (remainingTime === 0) return;
    if (isPaused) {
      setCountdown(Date.now() + (remainingTime ?? 0));
    } else {
      setIsPaused(true);
    }
  };

  return {
    countdown,
    isPaused,
    remainingTime,
    isFinished,
    setTimer,
    toggle,
    reset,
    setRemainingTime,
    setIsFinished,
    spentTime,
    setSpentTime,
    setIsPaused
  };
};
